import type { Condition, EntityType, Rule } from '@tunasong/schemas'
import type { FC, ReactNode } from 'react'
import { useState } from 'react'
import { Button } from '../primitives/button.js'
import { Chip } from '../primitives/chip.js'
import { Select } from '../primitives/select.js'
import {
  actionCatalogue,
  automationEntityTypeOptions,
  availableConditionKinds,
  builderEventNames,
  findAction,
  isKnownEntityType,
} from './rule-vocabulary.js'
import styles from './rule-editor.module.css'

export interface RuleEditorSlots {
  /** `purpose` tells the host which filter to apply; a `null` id means the Inbox. */
  renderEntityPicker: (props: {
    purpose: 'move-target' | 'space'
    value: string | null | undefined
    onChange: (id: string | null) => void
  }) => ReactNode
  renderAgentPicker: (props: { value: string | undefined; onChange: (definitionId: string) => void }) => ReactNode
}

interface TagConditionProps {
  tag: string
  /**
   * Disambiguates this tag's controls from every other rule's on the same
   * page, matching every other control in `RuleEditor` (see `ruleLabel`
   * there). Two rules filtering the same tag would otherwise both expose a
   * `Tag: x` / `Edit tag: x` / `Remove tag: x` with the identical name.
   */
  ruleLabel?: string
  onChange: (tag: string) => void
  onRemove: () => void
}

/**
 * A single `tag` condition's editable chip. Mirrors `RuleCard`'s tag-editing
 * affordance (`rule-card.tsx`) — a chip with an Edit button that swaps to a
 * text input, committing on Enter and on blur, reverting on Escape — so the
 * two surfaces behave identically.
 *
 * A freshly added condition arrives as `{ kind: 'tag', tag: '' }`. An empty
 * chip with an Edit button is a poor affordance (a chip showing nothing), so
 * an empty tag starts, and stays, in edit mode until it holds real text —
 * that is what makes it reachable without leaving the sentence.
 */
const TagCondition: FC<TagConditionProps> = ({ tag, ruleLabel, onChange, onRemove }) => {
  const [editing, setEditing] = useState(tag === '')
  const [draftTag, setDraftTag] = useState(tag)
  const displayTag = tag || 'new tag'
  const qualify = (label: string) => (ruleLabel ? `${label} (${ruleLabel})` : label)

  const commit = () => {
    const next = draftTag.trim()
    // An empty commit on an already-empty tag has nothing to fall back to —
    // stay in edit mode rather than show an empty, unclickable chip.
    setEditing(!next && tag === '')
    if (next && next !== tag) {
      onChange(next)
    }
  }

  return (
    <span>
      {editing ? (
        <input
          aria-label={qualify(`Tag: ${displayTag}`)}
          className={styles.tagInput}
          placeholder="Tag…"
          autoFocus={tag === ''}
          value={draftTag}
          onChange={event => setDraftTag(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              commit()
            }
            if (event.key === 'Escape') {
              setDraftTag(tag)
              setEditing(tag === '')
            }
          }}
          onBlur={commit}
        />
      ) : (
        <>
          <Chip label={tag} />
          <Button
            aria-label={qualify(`Edit tag: ${displayTag}`)}
            onClick={() => {
              setDraftTag(tag)
              setEditing(true)
            }}
          >
            Edit
          </Button>
        </>
      )}
      <Button aria-label={qualify(`Remove tag: ${displayTag}`)} onClick={onRemove}>
        ×
      </Button>
    </span>
  )
}

export interface RuleEditorProps extends RuleEditorSlots {
  rule: Rule
  /**
   * Disambiguates this rule's controls from every other rule's on the same
   * page, e.g. `"automation 2"`. With several rules rendered together, every
   * card would otherwise expose a control named plainly `Entity type` /
   * `Action` / etc., which is unusable for a screen-reader user navigating by
   * name. Omit it for single-rule usage, where there is nothing to
   * disambiguate.
   */
  ruleLabel?: string
  onEventChange: (event: Rule['event']) => void
  onTypeChange: (entityType: EntityType) => void
  onAddCondition: (kind: Condition['kind']) => void
  onRemoveCondition: (index: number) => void
  onTagChange: (index: number, tag: string) => void
  onActionChange: (commandId: string) => void
  onActionArgChange: (name: string, value: unknown) => void
  /** Space is a condition, not an action argument — it gets its own channel. */
  onSpaceChange: (spaceId: string | null) => void
  className?: string
}

/**
 * A rule rendered as an editable sentence. Every part is a control, and each
 * change is reported immediately — the host saves, this component holds no
 * draft state.
 *
 * The pickers arrive as slots because they live in the app and in plugin-lib;
 * ui-lib must not depend on either.
 */
export const RuleEditor: FC<RuleEditorProps> = props => {
  const { rule, ruleLabel, renderEntityPicker, renderAgentPicker, className } = props
  const qualify = (label: string) => (ruleLabel ? `${label} (${ruleLabel})` : label)

  const typeCondition = rule.conditions.find(condition => condition.kind === 'type')
  const spaceCondition = rule.conditions.find(condition => condition.kind === 'space')
  const spaceConditionIndex = rule.conditions.findIndex(condition => condition.kind === 'space')
  const action = rule.actions[0]
  const actionSpec = action ? findAction(action.commandId) : undefined
  const arg = actionSpec?.arg

  return (
    <div className={[styles.sentence, className].filter(Boolean).join(' ')}>
      <span>When a</span>

      <Select
        aria-label={qualify('Entity type')}
        value={typeCondition && typeCondition.kind === 'type' ? typeCondition.entityType : ''}
        onChange={event => props.onTypeChange(event.target.value as EntityType)}
      >
        {/*
         * A rule can only ever hold a valid `EntityType` (the schema enforces
         * it), but this control only offers the curated, human-labelled
         * subset a user should build automations against. Neither "no type
         * condition" (value `''`) nor a value outside that curated list —
         * e.g. a rule saved before it was curated — has a matching <option>
         * below otherwise, and the browser would then silently select
         * option 0, lying about what the rule actually holds.
         */}
        {!typeCondition ? (
          <option value="" disabled>
            Choose a type…
          </option>
        ) : null}
        {typeCondition && typeCondition.kind === 'type' && !isKnownEntityType(typeCondition.entityType) ? (
          <option value={typeCondition.entityType} disabled>
            {typeCondition.entityType} (unsupported)
          </option>
        ) : null}
        {automationEntityTypeOptions.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>

      <span>is</span>

      <Select
        aria-label={qualify('Event')}
        value={rule.event}
        onChange={event => props.onEventChange(event.target.value as Rule['event'])}
      >
        {builderEventNames.map(name => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </Select>

      {rule.conditions.map((condition, index) =>
        condition.kind === 'tag' ? (
          <TagCondition
            key={`tag-${index}`}
            tag={condition.tag}
            ruleLabel={ruleLabel}
            onChange={tag => props.onTagChange(index, tag)}
            onRemove={() => props.onRemoveCondition(index)}
          />
        ) : null
      )}

      {spaceCondition && spaceCondition.kind === 'space' ? (
        <span>
          {renderEntityPicker({
            purpose: 'space',
            value: spaceCondition.spaceId,
            onChange: props.onSpaceChange,
          })}
          <Button
            aria-label={qualify('Remove space filter')}
            onClick={() => props.onRemoveCondition(spaceConditionIndex)}
          >
            ×
          </Button>
        </span>
      ) : null}

      <Select
        aria-label={qualify('Add condition')}
        value=""
        onChange={event => props.onAddCondition(event.target.value as Condition['kind'])}
      >
        <option value="">Add condition</option>
        {availableConditionKinds(rule)
          .filter(kind => kind !== 'type')
          .map(kind => (
            <option key={kind} value={kind}>
              {kind}
            </option>
          ))}
      </Select>

      <span aria-hidden="true">→</span>

      <Select
        aria-label={qualify('Action')}
        value={action?.commandId ?? ''}
        onChange={event => props.onActionChange(event.target.value)}
      >
        {/*
         * `action.commandId` may be one this client's catalogue does not
         * know — written by a newer client. Without a matching <option> the
         * browser silently selects option 0 ("Convert to Song"), showing the
         * user a rule that does something else entirely. Rendering the raw
         * id honestly, disabled, is what tells the truth instead.
         */}
        {!action ? (
          <option value="" disabled>
            Choose an action…
          </option>
        ) : null}
        {action && !actionSpec ? (
          <option value={action.commandId} disabled>
            Unrecognised action: {action.commandId}
          </option>
        ) : null}
        {actionCatalogue.map(spec => (
          <option key={spec.commandId} value={spec.commandId}>
            {spec.label}
          </option>
        ))}
      </Select>

      {arg?.picker === 'entity'
        ? renderEntityPicker({
            purpose: 'move-target',
            value: action?.args[arg.name] as string | null | undefined,
            onChange: id => props.onActionArgChange(arg.name, id),
          })
        : null}

      {arg?.picker === 'agent'
        ? renderAgentPicker({
            value: action?.args[arg.name] as string | undefined,
            onChange: definitionId => props.onActionArgChange(arg.name, definitionId),
          })
        : null}
    </div>
  )
}
